import { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar';
import { useSession } from '@/context/SessionContext';
import { useProposal } from '@/context/ProposalContext';
import { getStandard } from '@/types/standards';
import { hasPhpBackend, checkPhpBackend } from '@/lib/phpApi';
import LiveClock from '@/components/LiveClock';
import MapBackdrop from '@/components/MapBackdrop';
import { useNotifications } from '@/hooks/useNotifications';

import { useIsMobile } from '@/hooks/use-mobile';
import {
  Search, Bell, Building2, Users, Database, Globe, AlertTriangle,
  Wifi, WifiOff, X, CheckCircle2, Info, AlertCircle, ChevronRight,
  FileText, Droplets, Beaker, Menu,
} from 'lucide-react';

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [dbConnected, setDbConnected] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const { currentCustomer, userProfile, activeStandard } = useSession();
  const { activeProposal, assessmentSession, exitProposalMode, endAssessmentSession } = useProposal();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();

  const standard = getStandard(activeStandard);
  const mainRef = useRef<HTMLElement>(null);

  // Reset scroll position to top on every route change
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    checkPhpBackend().then((result) => setDbConnected(result.available));
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K → search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      // Ctrl/Cmd + S → trigger save in current page context
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        // Dispatch a custom event that individual pages can listen to
        window.dispatchEvent(new CustomEvent('rb:save'));
      }
      // Ctrl/Cmd + P → print
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        e.preventDefault();
        window.print();
      }
      // Escape → close overlays
      if (e.key === 'Escape') { setSearchOpen(false); setBellOpen(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const pageTitles: Record<string, string> = {
    '/': 'Dashboard',
    '/parameters': 'Site Assessment',
    '/trends': 'Trends',
    '/reports': 'Reports',
    '/customers': 'Customers',
    '/samples': 'Samples',
    '/influent-entry': 'Add Influent Sample',
    '/influent': 'Influent Records',
    '/effluent-entry': 'Add Effluent Sample',
    '/effluent': 'Discharge Records',
    '/compliance-gateway': 'Compliance Gateway',
    '/standards': 'Standards',
    '/dosing': 'Dosing',
    '/process-db': 'Process Database',
    '/profile': 'User Profile',
    '/backend-setup': 'Backend Setup',
    '/equipment-validation': 'Equipment Validation',
    '/trend-reports': 'Trend Reports',
    '/admin': 'Admin Console',
    '/login': 'Login',
    '/instructions': 'Instructions',
    '/sample-workflow': 'Sample Workflow',
    '/effluent-reports': 'Effluent Reports',
    '/sa-water-charges': 'SA Water Charges',
  };

  const pageTitle = pageTitles[location.pathname] || 'Dashboard';

  const notifIcon = (type: string) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />;
      case 'error': return <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />;
      case 'success': return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />;
      default: return <Info className="w-3.5 h-3.5 text-cyan-400 shrink-0" />;
    }
  };

  const notifBorder = (type: string) => {
    switch (type) {
      case 'warning': return 'border-l-2 border-l-amber-500/60';
      case 'error': return 'border-l-2 border-l-red-500/60';
      case 'success': return 'border-l-2 border-l-emerald-500/60';
      default: return 'border-l-2 border-l-cyan-500/60';
    }
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <MapBackdrop />
      <Sidebar mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header Bar */}
        <header
          className="h-14 md:h-16 shrink-0 flex items-center justify-between px-3 md:px-5 border-b border-slate-800/80 backdrop-blur-md"
          style={{ background: 'rgba(var(--rb-canvas-rgb), 0.95)' }}
        >
          {/* LEFT: Hamburger (mobile) + Branding + Page Title */}
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all shrink-0"
              aria-label="Open navigation menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5 md:gap-2">
                <span className="text-rb-brand font-bold text-xs md:text-sm tracking-wide truncate">Reynolds & Bauhm</span>
                <span className="text-slate-600 hidden sm:inline">|</span>
                <span className="text-[10px] md:text-[11px] text-slate-400 font-medium tracking-wide hidden sm:inline">Smart Wastewater Assessment Tool</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <h1 className="text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">{pageTitle}</h1>
              </div>
            </div>
          </div>

          {/* CENTER: context ribbon — desktop only */}
          <div className="hidden lg:flex items-center gap-3">
            <AnimatePresence mode="popLayout">
              {userProfile?.companyName && (
                <motion.div
                  key="company"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800/60 border border-slate-800/50"
                >
                  <Building2 className="w-3 h-3 text-rb-brand" />
                  <span className="text-[11px] text-slate-300 font-medium truncate max-w-[140px]">
                    {userProfile.companyName}
                  </span>
                </motion.div>
              )}

              {currentCustomer && (
                <motion.div
                  key="customer"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/5 border border-cyan-500/20"
                >
                  <Users className="w-3 h-3 text-cyan-400" />
                  <span className="text-[11px] text-cyan-300 font-medium truncate max-w-[160px]">
                    {currentCustomer.company || currentCustomer.name}
                  </span>
                </motion.div>
              )}

              <motion.div
                key="standard"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${
                  standard.available
                    ? 'bg-emerald-500/5 border-emerald-500/20'
                    : 'bg-slate-800/40 border-slate-800/40'
                }`}
              >
                <Globe className="w-3 h-3 text-emerald-400" />
                <span className="text-[11px] font-medium">{standard.flag}</span>
                <span className="text-[11px] text-slate-300 font-medium">{standard.shortName}</span>
                {!standard.available && (
                  <span className="text-[10px] text-slate-500">({standard.comingSoonText || 'Soon'})</span>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* RIGHT: clock, search, bell, db status */}
          <div className="flex items-center gap-1.5 md:gap-3">
            <div className="hidden sm:block"><LiveClock /></div>

            {/* DB Connection Status — compact on mobile */}
            <div className={`flex items-center gap-1 md:gap-1.5 px-1.5 md:px-2.5 py-1 rounded-md border ${
              dbConnected
                ? 'bg-emerald-500/5 border-emerald-500/20'
                : 'bg-red-500/5 border-red-500/20'
            }`} title={dbConnected ? 'MySQL database connected' : 'Database offline — data saved locally'}>
              {dbConnected ? (
                <Wifi className="w-3 h-3 text-emerald-400" />
              ) : (
                <WifiOff className="w-3 h-3 text-red-400" />
              )}
              <Database className="w-3 h-3 text-slate-400 hidden sm:block" />
              <span className={`text-[10px] font-medium hidden sm:block ${dbConnected ? 'text-emerald-400' : 'text-red-400'}`}>
                {dbConnected ? 'DB' : 'Local'}
              </span>
              <span className={`w-1.5 h-1.5 rounded-full ${dbConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
            </div>

            {/* Search button — full on desktop, icon-only on tablet, hidden on mobile */}
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-500 border border-slate-700 hover:border-slate-600 hover:text-slate-300 transition-all min-w-[180px]"
            >
              <Search className="w-4 h-4" />
              <span>Search...</span>
              <kbd className="ml-auto text-[10px] text-slate-600 font-mono bg-slate-800 px-1.5 rounded">⌘K</kbd>
            </button>
            {/* Search icon-only — mobile */}
            <button
              onClick={() => setSearchOpen(true)}
              className="sm:hidden flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 transition-all"
              aria-label="Search"
            >
              <Search className="w-4 h-4" />
            </button>

            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setBellOpen(!bellOpen)}
                className="relative p-2 text-slate-500 hover:text-slate-300 transition-colors rounded-lg hover:bg-slate-800/50"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold px-1">
                    {unreadCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {bellOpen && (
                  <>
                    <div className="fixed inset-0 z-[90]" onClick={() => setBellOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 w-[min(calc(100vw-1rem),380px)] rounded-xl border border-slate-700 shadow-2xl z-[100] overflow-hidden"
                      style={{ background: 'rgba(var(--rb-canvas-rgb), 0.98)' }}
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-rb-brand" />
                          <span className="text-sm font-semibold text-slate-200">Notifications</span>
                          {unreadCount > 0 && (
                            <span className="px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold">
                              {unreadCount} new
                            </span>
                          )}
                        </div>
                        {notifications.length > 0 && (
                          <button
                            onClick={markAllRead}
                            className="text-[11px] text-slate-500 hover:text-cyan-400 transition-colors"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>

                      <div className="max-h-[400px] overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <Bell className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">No notifications yet</p>
                            <p className="text-[11px] text-slate-600 mt-1">Important alerts and hints will appear here</p>
                          </div>
                        ) : (
                          notifications.map((n) => (
                            <div
                              key={n.id}
                              className={`px-4 py-3 border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors cursor-pointer ${notifBorder(n.type)} ${n.read ? 'opacity-60' : ''}`}
                              onClick={() => markRead(n.id)}
                            >
                              <div className="flex items-start gap-2.5">
                                {notifIcon(n.type)}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-slate-200">{n.title}</span>
                                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-rb-brand shrink-0" />}
                                  </div>
                                  <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{n.message}</p>
                                  {n.action && (
                                    <div className="flex items-center gap-1 mt-1.5 px-2 py-1 rounded bg-slate-800/50">
                                      <ChevronRight className="w-3 h-3 text-rb-brand" />
                                      <span className="text-[10px] text-slate-300">{n.action}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <span className="w-2 h-2 rounded-full bg-emerald-500 rb-pulse-dot" />
              <span className="text-xs text-emerald-400 font-medium">Live</span>
            </div>
          </div>
        </header>

        {/* Proposal Mode Banner */}
        <AnimatePresence>
          {activeProposal && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="shrink-0 flex items-center justify-between px-5 py-2 bg-violet-500/10 border-b border-violet-500/30"
            >
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
                <FileText className="w-3.5 h-3.5 text-violet-400" />
                <span className="text-xs font-bold text-violet-300">PROPOSAL MODE</span>
                <span className="font-mono text-xs text-violet-200 border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 rounded">
                  {activeProposal.proposalNumber}
                </span>
                <span className="text-[11px] text-slate-400 truncate max-w-[200px] hidden md:block">{activeProposal.title}</span>
                {activeProposal.isLocalOnly && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 font-bold">LOCAL ONLY</span>
                )}
                {activeProposal.customerName && (
                  <span className="text-[10px] text-slate-500 hidden lg:flex items-center gap-1">
                    <Users className="w-3 h-3" />{activeProposal.customerName}
                  </span>
                )}
              </div>
              <button
                onClick={exitProposalMode}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20 text-[10px] font-bold transition-all"
              >
                <X className="w-3 h-3" /> Exit Proposal Mode
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Local Draft Banner */}
        <AnimatePresence>
          {activeProposal?.isLocalOnly && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="shrink-0 flex items-center gap-3 px-5 py-1.5 bg-slate-700/25 border-b border-slate-600/30"
            >
              <WifiOff className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Local Draft — Not Saved to Database</span>
              <span className="text-[10px] text-slate-600">Samples and data are stored in this browser only</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Assessment Session Banner */}
        <AnimatePresence>
          {assessmentSession && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`shrink-0 flex items-center justify-between px-5 py-2 border-b ${
                assessmentSession.type === 'influent'
                  ? 'bg-sky-500/8 border-sky-500/20'
                  : 'bg-teal-500/8 border-teal-500/20'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full animate-pulse ${assessmentSession.type === 'influent' ? 'bg-sky-400' : 'bg-teal-400'}`} />
                {assessmentSession.type === 'influent' ? <Droplets className="w-3.5 h-3.5 text-sky-400" /> : <Beaker className="w-3.5 h-3.5 text-teal-400" />}
                <span className={`text-xs font-bold ${assessmentSession.type === 'influent' ? 'text-sky-300' : 'text-teal-300'}`}>
                  {assessmentSession.type === 'influent' ? 'INFLUENT' : 'EFFLUENT'} ASSESSMENT MODE
                </span>
                {assessmentSession.customerName && (
                  <span className="text-[10px] text-slate-500 flex items-center gap-1">
                    <Users className="w-3 h-3" />{assessmentSession.customerName}
                  </span>
                )}
              </div>
              <button
                onClick={endAssessmentSession}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg border text-[10px] font-bold transition-all ${
                  assessmentSession.type === 'influent'
                    ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 hover:bg-sky-500/20'
                    : 'bg-teal-500/10 text-teal-400 border-teal-500/20 hover:bg-teal-500/20'
                }`}
              >
                <X className="w-3 h-3" /> End Session
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Red "No active customer" banner */}
        <AnimatePresence>
          {!currentCustomer && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="shrink-0 flex items-center justify-between px-3 md:px-5 py-2 bg-red-500/5 border-b border-red-500/20"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-xs text-red-400 font-medium">
                  <span className="hidden sm:inline">No active customer selected — </span>samples and reports will not be assigned
                </span>
              </div>
              <button
                onClick={() => { navigate('/customers'); }}
                className="text-[11px] px-3 py-1 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors font-bold"
              >
                Select Customer
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content area — consistent centering for all routes.
             max-w-[1440px] mx-auto gives centred layout on wide monitors while
             using all available space up to 1440 px.  Pages that need a narrower
             inner container (e.g. UserProfilePage max-w-3xl) keep their own
             class; it simply nests inside this wrapper.
             h-full passes the flex-1 height down to pages that use lg:h-full
             (e.g. CustomersPage with its internally-scrollable list panel). */}
        <main ref={mainRef} className="flex-1 overflow-x-hidden overflow-y-auto p-3 md:p-6" style={{ scrollbarGutter: 'stable' }}>
          <div className="max-w-[1440px] mx-auto w-full h-full">
            <Outlet />
          </div>
        </main>

        {/* Footer Disclaimer */}
        <footer className="shrink-0 border-t border-slate-800 bg-rb-canvas px-6 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-600">
            <div className="flex items-center gap-2">
              <span className="text-rb-brand font-bold">Reynolds & Bauhm</span>
              <span>|</span>
              <span>Smart Wastewater Assessment Tool v2.0</span>
            </div>
            <div className="flex items-center gap-3">
              <span>© {new Date().getFullYear()} All rights reserved</span>
              <span>|</span>
              <span className="text-amber-500/60">Assessment tool — not for final design</span>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 leading-relaxed max-w-4xl">
            © {new Date().getFullYear()} Reynolds & Bauhm. Proprietary & Confidential. Unauthorised copying or distribution is prohibited. For preliminary assessment only — not for final engineering design. Engage a licensed wastewater engineer before capital expenditure.
          </p>
        </footer>
      </div>

      {/* Command Palette */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-start justify-center pt-4 md:pt-20 px-3 md:px-0"
            onClick={() => setSearchOpen(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl rounded-xl shadow-2xl border border-slate-700 overflow-hidden"
              style={{ background: 'var(--bg-surface)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-700">
                <Search className="w-5 h-5 text-slate-500" />
                <input
                  autoFocus
                  placeholder="Search pages, customers, samples..."
                  className="flex-1 bg-transparent text-white text-lg placeholder-slate-500 border-none outline-none focus:ring-0"
                  onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
                />
              </div>
              <div className="p-2">
                <div className="text-xs text-slate-500 px-3 py-2">Quick Navigation</div>
                {Object.entries(pageTitles).filter(([k]) => k !== '/login').map(([path, label]) => (
                  <button
                    key={path}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800/50 hover:text-cyan-400 transition-colors text-left"
                    onClick={() => { setSearchOpen(false); navigate(path); }}
                  >
                    <span className="text-slate-500">#</span>
                    {label}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
